<template>
  <div v-if="!isHubUser">
    <v-radio-group
      v-model="isLocationOwnedByCurrentOrganizationToggle"
      mandatory
      @change="onOwnerRadioChanged"
    >
      <template #label>
        <vs-heading type="subhead">Is the location owned by one of your clients? </vs-heading>
      </template>
      <v-radio label="Yes" :value="true"></v-radio>
      <v-radio label="No" :value="false"></v-radio>
    </v-radio-group>
    <vs-wrapper v-if="!isLocationOwnedByCurrentOrganizationToggle">
      <v-checkbox
        v-model="privateToggle"
        label="Private"
        hint="Only your company and your clients see this location"
        class="mt-0"
        persistent-hint
        @change="onPrivateChanged"
      ></v-checkbox>
      <vs-autocomplete
        class="mt-2"
        :items="locationHubOptions"
        :disabled="disabled"
        item-text="name"
        item-value="id"
        label="Which hubs do you use this location with?"
        :value="location.accessBusinessUnitIds"
        multiselect
        @input="
          handleLocationUpdate({
            field: 'accessBusinessUnitIds',
            value: $event,
          })
        "
      />
    </vs-wrapper>
    <vs-wrapper v-else>
      <vs-autocomplete
        :items="locationOrganizationOptions"
        :disabled="disabled"
        item-text="name"
        item-value="id"
        :value="location.ownerId"
        label="Client"
        @input="onClientChanged"
      />
      <vs-autocomplete
        v-if="location.ownerId"
        :items="locationHubOptionsFilteredByOwnerId"
        :disabled="disabled"
        item-text="name"
        item-value="id"
        :label="availableInTheChosenClientLabel"
        multiselect
        :value="location.accessBusinessUnitIds"
        @input="
          handleLocationUpdate({
            field: 'accessBusinessUnitIds',
            value: $event,
          })
        "
      />
    </vs-wrapper>
  </div>
  <div v-else>
    <vs-autocomplete
      v-if="managedCompanies"
      :items="managedCompanies"
      :value="location.ownerId"
      :disabled="disabled"
      label="Company"
      placeholder="Select a company"
      required
      @input="handleLocationUpdate({ field: 'ownerId', value: $event })"
    />
  </div>
</template>

<script setup lang="ts">
import VsHeading from '@/components/vision/VsHeading.vue'
import VsAutocomplete from '@/components/vision/Inputs/VsAutocomplete/VsAutocomplete.vue'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import { useStore } from 'vuex-composition-helpers'
import { computed, ref, watch } from 'vue'
import { useCurrentOrg } from '@/common/useCurrentOrg'
import { useGetAssignableHubs } from '@/api/hubs'
import { useGetGuestConnectedOrganizationToThisOrganization } from '@/api/organizations'
import { useGetManagedCompaniesOptions } from '@/api/options'

const props = defineProps<{
  location: {
    ownerId?: string | null
    canEdit?: boolean | null
    private?: boolean | null
    accessBusinessUnitIds?: string[] | null
  }
}>()

const store = useStore()
const isHubUser = computed(() => store.getters['permissions/isHostOrganizationUser'])
const isLocationOwnedByCurrentOrganizationToggle = ref(false)
const privateToggle = ref(!!props.location.private)
const { currentOrg } = useCurrentOrg()
const currentOrgId = computed(() => currentOrg.value?.id)

const disabled = computed(() => !props.location.canEdit)

const { data: locationHubOptions } = useGetAssignableHubs(currentOrgId)
const { data: locationOrganizationOptions } =
  useGetGuestConnectedOrganizationToThisOrganization(currentOrgId)

const availableInTheChosenClientLabel = computed(() => {
  const clientName = locationOrganizationOptions.value?.find(
    (o) => o.id === props.location.ownerId,
  )?.name
  return `Available in ${clientName}`
})

const locationHubOptionsFilteredByOwnerId = computed(() =>
  locationHubOptions.value?.filter((o) => o.organizationId === props.location.ownerId),
)

function onOwnerRadioChanged(e: boolean) {
  if (!e) {
    handleLocationUpdate({
      field: 'ownerId',
      value: currentOrg.value?.id,
    })
  } else {
    handleLocationUpdate({
      field: 'ownerId',
      value: null,
    })
  }
  handleLocationUpdate({
    field: 'private',
    value: false,
  })
  handleLocationUpdate({
    field: 'accessBusinessUnitIds',
    value: [],
  })
}

function onPrivateChanged(e: boolean) {
  handleLocationUpdate({
    field: 'private',
    value: e,
  })
}

function onClientChanged(e: string) {
  handleLocationUpdate({ field: 'ownerId', value: e })
  handleLocationUpdate({ field: 'accessBusinessUnitIds', value: [] })
}

watch(
  () => props.location.ownerId,
  (newVal) => {
    isLocationOwnedByCurrentOrganizationToggle.value = newVal !== currentOrg.value?.id
  },
  { immediate: true },
)
watch(
  () => props.location.private,
  (newVal) => {
    privateToggle.value = !!newVal
  },
  { immediate: true },
)

const emit = defineEmits<{
  (e: 'updated', data: { field: string; value: any }): void
}>()
function handleLocationUpdate(data: { field: string; value: any }) {
  emit('updated', data)
}

const { data: managedCompanies } = useGetManagedCompaniesOptions()
watch(managedCompanies, (next) => {
  // once the companies list has loaded,
  // if there is only one company in the list
  // and there is no location owner,
  // set the location owner to the single company we have.

  if (props.location.ownerId === null && next?.length === 1 && 'value' in next[0]) {
    handleLocationUpdate({
      field: 'ownerId',
      value: next[0].value,
    })
  }
})
</script>
